import React, { useState, useEffect, useRef } from "react";
import { Btn } from "./atoms";
import { addShow, createSalesAssignment, createMarketingAssignment, getEvents, getUsers, getEventReviewers } from "../services/api";
import { Folder, Calendar, Megaphone, ClipboardList, Zap } from "lucide-react";

// ─────────────────────────────────────────────────────────────────────────────
// Add Competitor Show — the full working form, extracted from ShowsPage so both
// the Portfolio page and the tracker use the exact same modal (single + bulk,
// name autocomplete, duplicate-year guard, date-range + schedule calendars,
// reviewer assignment). Self-contained; driven by props.
//
//   open           — render when true
//   defaultEventId — pre-select an ITE event (e.g. from "Start tracking")
//   existingShows  — [{ name, year }] for autocomplete + duplicate detection
//   scheduledBy    — acting user id (created_by / scheduled_by)
//   onToast(msg,t) — surface success/error (optional)
//   onClose()      — dismiss
//   onAdded()      — called after a successful add so the parent can refresh
// ─────────────────────────────────────────────────────────────────────────────

const F = "'Montserrat',sans-serif";
const M = "'Montserrat',sans-serif";
const THIS_YEAR = new Date().getFullYear();

// Features hidden for now (implementations below are kept intact — flip to re-enable).
const ENABLE_BULK_UPLOAD    = false;  // "Bulk upload" tab
const ENABLE_SCHEDULE_LATER = false;  // "Schedule later" enrichment option
const MONTHS_LONG = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEK_DAYS   = ["Su","Mo","Tu","We","Th","Fr","Sa"];

const ADD_FORM_DEFAULT = {
  mode:"single", eventId:"", name:"", year: String(THIS_YEAR),
  location:"", startDate:"", endDate:"",
  source:"Link", url:"", fileName:"", fileObj:null, bulkFile:"", bulkFileObj:null,
  runWhen:"now", scheduleDate:"", mktReviewer:"", salesReviewer:"",
};

const inp = {width:"100%",padding:"9px 12px",borderRadius:8,border:"1px solid #D8DADC",fontSize:12,fontFamily:F,outline:"none",boxSizing:"border-box"};
const lab = {fontSize:11,fontWeight:600,color:"#3F3F46",display:"block",marginBottom:5};

function DateRangePicker({ start, end, onChange, hasError }) {
  const [open, setOpen]       = useState(false);
  const [hovered, setHovered] = useState(null);
  const [picking, setPicking] = useState("start");
  const [calPos, setCalPos]   = useState({ top: 0, left: 0 });
  const [viewDate, setViewDate] = useState(() => {
    const d = start ? new Date(start + "T12:00:00") : new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const triggerRef = useRef(null);
  const calRef     = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (triggerRef.current?.contains(e.target) || calRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const openCal = () => {
    if (triggerRef.current) {
      const r = triggerRef.current.getBoundingClientRect();
      setCalPos({ top: r.bottom + 6, left: r.left });
    }
    setPicking("start"); setHovered(null); setOpen(o => !o);
  };

  const fmtD = (s) => s ? new Date(s + "T12:00:00").toLocaleDateString("en-GB", {day:"2-digit",month:"short",year:"numeric"}) : "";
  const effectiveEnd = picking === "end" && hovered && hovered > (start||"") ? hovered : end;

  const dayClick = (ds) => {
    if (picking === "start" || !start || ds < start) {
      onChange(ds, ""); setPicking("end");
    } else {
      onChange(start, ds); setOpen(false); setPicking("start"); setHovered(null);
    }
  };

  const renderDays = (y, m) => {
    const firstDow = new Date(y, m, 1).getDay();
    const totalDays = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(<div key={`b${i}`}/>);
    for (let d = 1; d <= totalDays; d++) {
      const ds = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const isS = ds === start, isE = ds === effectiveEnd;
      const inR = !!(start && effectiveEnd && ds > start && ds < effectiveEnd);
      cells.push(
        <div key={d}
          onMouseEnter={() => picking==="end" && setHovered(ds)}
          onClick={() => dayClick(ds)}
          style={{
            height:30, display:"flex", alignItems:"center", justifyContent:"center",
            fontSize:12, cursor:"pointer", userSelect:"none",
            borderRadius: isS||isE ? 6 : 0,
            background: isS||isE ? "#E8431A" : inR ? "#FCEBE5" : "transparent",
            color: isS||isE ? "#fff" : inR ? "#C23417" : "#26262A",
            fontWeight: isS||isE ? 700 : 400,
          }}
        >{d}</div>
      );
    }
    return cells;
  };

  const prevM = () => setViewDate(p => p.m===0 ? {y:p.y-1,m:11} : {y:p.y,m:p.m-1});
  const nextM = () => setViewDate(p => p.m===11 ? {y:p.y+1,m:0} : {y:p.y,m:p.m+1});

  const displayLabel = start && end
    ? `${fmtD(start)}  –  ${fmtD(end)}`
    : start ? `${fmtD(start)}  →  pick end date`
    : "Select date range";

  return (
    <div style={{position:"relative"}}>
      <div ref={triggerRef} onClick={openCal} style={{
        padding:"9px 12px", border:`1px solid ${hasError?"#C1121F":open?"#E8431A":"#D8DADC"}`,
        borderRadius:8, fontSize:12, fontFamily:F, cursor:"pointer", background:"#fff",
        display:"flex", alignItems:"center", gap:8,
        color: start ? "#26262A" : "#818084",
        boxShadow: open ? "0 0 0 3px #FCEBE5" : "none",
      }}>
        <Calendar size={14} color={open?"#E8431A":"#818084"} style={{flexShrink:0}}/>
        <span style={{flex:1}}>{displayLabel}</span>
        {(start||end) && (
          <span
            onMouseDown={e=>{e.stopPropagation();onChange("","");setPicking("start");setHovered(null);setOpen(false);}}
            style={{fontSize:13,color:"#818084",cursor:"pointer",lineHeight:1,flexShrink:0}}
          >✕</span>
        )}
      </div>
      {open && (
        <div ref={calRef} onMouseLeave={()=>setHovered(null)} style={{
          position:"fixed", top:calPos.top, left:calPos.left, zIndex:9999,
          background:"#fff", border:"1px solid #D8DADC", borderRadius:12,
          boxShadow:"0 10px 40px rgba(0,0,0,0.18)", padding:"16px 18px", width:272,
        }}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
            <button onClick={prevM} style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",fontSize:18,color:"#818084",lineHeight:1}}>‹</button>
            <span style={{fontSize:13,fontWeight:700,fontFamily:M}}>{MONTHS_LONG[viewDate.m]} {viewDate.y}</span>
            <button onClick={nextM} style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",fontSize:18,color:"#818084",lineHeight:1}}>›</button>
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2}}>
            {WEEK_DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:9,fontWeight:700,color:"#818084",padding:"2px 0"}}>{d}</div>)}
          </div>
          <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
            {renderDays(viewDate.y, viewDate.m)}
          </div>
          <div style={{marginTop:10,fontSize:10,color:"#818084",textAlign:"center",fontStyle:"italic"}}>
            {picking==="start" ? "Click a date to set the start" : "Now click to set the end date"}
          </div>
        </div>
      )}
    </div>
  );
}

// Popup-only calendar — no trigger div; parent controls open/pos
function SingleDatePicker({ value, onChange, open, onClose, pos }) {
  const [viewDate, setViewDate] = useState(() => {
    const d = value ? new Date(value + "T12:00:00") : new Date();
    return { y: d.getFullYear(), m: d.getMonth() };
  });
  const calRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (!calRef.current?.contains(e.target)) onClose(); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderDays = (y, m) => {
    const firstDow  = new Date(y, m, 1).getDay();
    const totalDays = new Date(y, m + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < firstDow; i++) cells.push(<div key={`b${i}`}/>);
    for (let d = 1; d <= totalDays; d++) {
      const ds    = `${y}-${String(m+1).padStart(2,"0")}-${String(d).padStart(2,"0")}`;
      const isSel = ds === value;
      cells.push(
        <div key={d} onClick={() => { onChange(ds); onClose(); }} style={{
          height:30, display:"flex", alignItems:"center", justifyContent:"center",
          fontSize:12, cursor:"pointer", userSelect:"none", borderRadius:6,
          background: isSel ? "#E8431A" : "transparent",
          color:      isSel ? "#fff"    : "#26262A",
          fontWeight: isSel ? 700       : 400,
        }}>{d}</div>
      );
    }
    return cells;
  };

  const prevM = () => setViewDate(p => p.m===0  ? {y:p.y-1,m:11} : {y:p.y,m:p.m-1});
  const nextM = () => setViewDate(p => p.m===11 ? {y:p.y+1,m:0}  : {y:p.y,m:p.m+1});

  if (!open) return null;
  return (
    <div ref={calRef} style={{
      position:"fixed", top:pos.top, left:pos.left, zIndex:9999,
      background:"#fff", border:"1px solid #D8DADC", borderRadius:12,
      boxShadow:"0 10px 40px rgba(0,0,0,0.18)", padding:"16px 18px", width:272,
    }}>
      <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:10}}>
        <button onClick={prevM} style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",fontSize:18,color:"#818084",lineHeight:1}}>‹</button>
        <span style={{fontSize:13,fontWeight:700,fontFamily:M}}>{MONTHS_LONG[viewDate.m]} {viewDate.y}</span>
        <button onClick={nextM} style={{background:"none",border:"none",cursor:"pointer",padding:"4px 8px",fontSize:18,color:"#818084",lineHeight:1}}>›</button>
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2,marginBottom:2}}>
        {WEEK_DAYS.map(d=><div key={d} style={{textAlign:"center",fontSize:9,fontWeight:700,color:"#818084",padding:"2px 0"}}>{d}</div>)}
      </div>
      <div style={{display:"grid",gridTemplateColumns:"repeat(7,1fr)",gap:2}}>
        {renderDays(viewDate.y, viewDate.m)}
      </div>
    </div>
  );
}

const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-GB", {day:"2-digit",month:"short",year:"numeric"}); } catch { return d; } };
const isValidUrl = (s) => { try { const u = new URL(s); return u.protocol === "https:" || u.protocol === "http:"; } catch { return false; } };
const toISODate = (s) => {
  if (!s || !s.trim()) return undefined;
  if (/^\d{4}-\d{2}-\d{2}$/.test(s.trim())) return s.trim();
  const d = new Date(s.trim());
  if (!isNaN(d)) return d.toISOString().split("T")[0];
  return undefined;
};
const parseBulkShowsCSV = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onerror = reject;
  reader.onload = e => {
    const parseRow = row => {
      const cols = []; let cur = "", inQ = false;
      for (const ch of row) {
        if (ch === '"') inQ = !inQ;
        else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ""; }
        else cur += ch;
      }
      cols.push(cur.trim());
      return cols;
    };
    const lines = e.target.result.split(/\r?\n/).filter(l => l.trim());
    const headers = parseRow(lines[0]).map(h => h.toLowerCase());
    const rows = lines.slice(1)
      .map(l => parseRow(l))
      .filter(cols => cols.some(c => c))
      .map(cols => { const o = {}; headers.forEach((h, i) => o[h] = cols[i] || ""); return o; });
    resolve(rows);
  };
  reader.readAsText(file);
});
const downloadBulkTemplate = () => {
  const headers = ["Show Name","Year","Location","Start Date (YYYY-MM-DD)","End Date (YYYY-MM-DD)","Exhibitor List URL","Enrichment (add_to_queue / schedule_later)","Scheduled Date (YYYY-MM-DD, only if schedule_later)"];
  const example = ["Gulfood","2026","Dubai, UAE","2026-02-17","2026-02-21","https://www.gulfood.com/exhibitors/2026","add_to_queue",""];
  const csv = [headers, example].map(r => r.map(v => `"${v}"`).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "competitor_shows_template.csv"; a.click();
  URL.revokeObjectURL(url);
};

export default function AddShowModal({ open, defaultEventId, existingShows = [], scheduledBy, onToast, onClose, onAdded }) {
  const [addForm, setAddForm]             = useState(ADD_FORM_DEFAULT);
  const [submitting, setSubmitting]       = useState(false);
  const [modalEvents, setModalEvents]     = useState([]);
  const [mktUsers, setMktUsers]           = useState([]);
  const [salesUsers, setSalesUsers]       = useState([]);
  const [nameDropdownOpen, setNameDropdownOpen] = useState(false);
  const [schedCalOpen, setSchedCalOpen]   = useState(false);
  const [schedCalPos, setSchedCalPos]     = useState({ top: 0, left: 0 });
  const [runTip, setRunTip]               = useState(null);
  const bulkFileRef = useRef(null);
  const schedBtnRef = useRef(null);

  const toast = (m, t) => onToast?.(m, t);

  useEffect(() => {
    if (!open) return;
    setAddForm({ ...ADD_FORM_DEFAULT, eventId: defaultEventId || "" });
    setNameDropdownOpen(false); setSchedCalOpen(false); setSubmitting(false);
    getEvents().then(es => {
      setModalEvents(Array.isArray(es) ? es : []);
      setAddForm(p => ({ ...p, eventId: p.eventId || es?.[0]?.ite_event_id || "" }));
    }).catch(() => {});
    // Load the selectable reviewer options (all managers of each role). The actual
    // selection is driven separately by the chosen event's designated reviewers
    // (event_sales_reviewers / event_marketing_reviewers) — see the effect below.
    getUsers({ role: "marketing_manager" }).then(d => setMktUsers(Array.isArray(d) ? d : [])).catch(() => {});
    getUsers({ role: "sales_manager" }).then(d => setSalesUsers(Array.isArray(d) ? d : [])).catch(() => {});
  }, [open, defaultEventId]);

  // Pre-select the first designated Sales & Marketing reviewer for the chosen ITE event
  // from event_sales_reviewers / event_marketing_reviewers (GET /events/{id}/reviewers).
  // Re-runs whenever the selected event changes; falls back to "" when no mapping exists.
  useEffect(() => {
    if (!open) return;
    const evId = addForm.eventId;
    if (!evId) { setAddForm(p => ({ ...p, mktReviewer: "", salesReviewer: "" })); return; }
    getEventReviewers(evId)
      .then(r => setAddForm(p => ({
        ...p,
        salesReviewer: r?.sales?.[0]?.user_id     || "",
        mktReviewer:   r?.marketing?.[0]?.user_id  || "",
      })))
      .catch(() => setAddForm(p => ({ ...p, mktReviewer: "", salesReviewer: "" })));
  }, [open, addForm.eventId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open) return null;

  const schedBy = scheduledBy || "system";

  const submitAdd = () => {
    const evId = addForm.eventId || defaultEventId || "";
    const urlVal = addForm.url.trim();
    if (addForm.mode === "single" && urlVal && !isValidUrl(urlVal)) {
      toast("Please enter a valid URL (must start with https:// or http://)", "error"); return;
    }
    if (addForm.mode === "single" && addForm.startDate && addForm.endDate && addForm.endDate < addForm.startDate) {
      toast("End date cannot be before start date.", "error"); return;
    }
    const canAdd = !!evId
      && (addForm.mode === "single" ? addForm.name.trim() : !!addForm.bulkFileObj)
      && (addForm.runWhen === "now" || addForm.scheduleDate);
    if (!canAdd || submitting) return;

    const done = () => { setSubmitting(false); setAddForm(ADD_FORM_DEFAULT); onClose(); };
    const sched = addForm.runWhen === "now" ? "Now" : fmtDate(addForm.scheduleDate);
    setSubmitting(true);

    if (addForm.mode === "single") {
      const showName = addForm.name.trim();
      const schedVal = addForm.runWhen === "now" ? "run_now" : "scheduled";
      addShow({
        ite_event_id:  evId,
        name:          showName,
        year:          parseInt(addForm.year) || undefined,
        location:      addForm.location    || undefined,
        start_date:    addForm.startDate   || undefined,
        end_date:      addForm.endDate     || undefined,
        website:       urlVal || undefined,
        schedule:      schedVal,
        scheduled_at:  addForm.runWhen === "schedule" ? addForm.scheduleDate : undefined,
        scheduled_by:  schedBy,
      })
        .then((res) => {
          const assignCalls = [];
          if (addForm.salesReviewer)
            assignCalls.push(createSalesAssignment({ show_id: res.show_id, owner_id: addForm.salesReviewer, review_type: "online_review", created_by: schedBy }));
          if (addForm.mktReviewer)
            assignCalls.push(createMarketingAssignment({ show_id: res.show_id, owner_id: addForm.mktReviewer, created_by: schedBy }));
          return Promise.all(assignCalls);
        })
        .then(() => {
          toast(addForm.runWhen === "now"
            ? `${showName} added · enrichment queued`
            : `${showName} added · enrichment scheduled for ${sched}`);
          onAdded?.();
          done();
        })
        .catch(() => { toast("Failed to add show — please try again.", "error"); setSubmitting(false); });
    } else {
      parseBulkShowsCSV(addForm.bulkFileObj).then(rows => {
        if (!rows.length) { toast("File has no data rows.", "error"); setSubmitting(false); return; }
        let firstErr = null;
        return Promise.all(rows.map(r => addShow({
          ite_event_id: evId,
          name:         r["show name"]              || r["show_name"] || "",
          location:     r["location"]               || undefined,
          start_date:   toISODate(r["start date (yyyy-mm-dd)"]),
          end_date:     toISODate(r["end date (yyyy-mm-dd)"]),
          website:      r["exhibitor list url"]     || undefined,
          schedule:     (r["enrichment (add_to_queue / schedule_later)"] || "add_to_queue").trim() === "add_to_queue" ? "run_now" : "tonight",
          scheduled_by: schedBy,
        }).catch(err => { if (!firstErr) firstErr = err?.message || String(err); return null; })))
        .then(res => Object.assign(res, { __firstErr: firstErr }));
      })
      .then(results => {
        if (!results) return;
        const ok = results.filter(Boolean).length;
        const errMsg = results.__firstErr;
        if (ok === 0 && errMsg) { toast(`Import failed: ${errMsg}`, "error"); setSubmitting(false); return; }
        toast(addForm.runWhen === "now"
          ? `${ok} shows added · enrichment queued`
          : `${ok} shows added · enrichment scheduled for ${sched}`);
        onAdded?.();
        done();
      })
      .catch(() => { toast("Failed to read file — ensure it matches the template.", "error"); setSubmitting(false); });
    }
  };

  const evId = addForm.eventId || defaultEventId || "";
  const dateErr = addForm.mode==="single" && addForm.startDate && addForm.endDate && addForm.endDate < addForm.startDate;

  // canonical name → years map (autocomplete + duplicate detection)
  const canonicalMap = {};
  existingShows.forEach(s => {
    const canonical = (s.name || "").replace(/\s*20\d{2}\s*$/i, '').trim();
    if (!canonical) return;
    if (!canonicalMap[canonical]) canonicalMap[canonical] = { canonical, years: [] };
    if (s.year) canonicalMap[canonical].years.push(s.year);
  });
  const nameQuery = addForm.name.trim().toLowerCase();
  const nameSuggestions = nameQuery.length >= 2
    ? Object.values(canonicalMap).filter(s => s.canonical.toLowerCase().includes(nameQuery))
    : [];
  const matchedShow = Object.values(canonicalMap).find(s => s.canonical.toLowerCase() === addForm.name.trim().toLowerCase());
  const yearAlreadyTracked = !!(matchedShow && matchedShow.years.map(String).includes(String(addForm.year)));

  const canAdd = !!evId
    && (addForm.mode==="single" ? !!addForm.name.trim() : !!addForm.bulkFile)
    && (addForm.runWhen==="now" || addForm.scheduleDate)
    && !dateErr
    && !yearAlreadyTracked;

  return (
    <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:999}}>
      <div style={{background:"#fff",borderRadius:14,width:540,maxHeight:"90vh",overflow:"auto",padding:"26px 28px",boxShadow:"0 20px 60px rgba(0,0,0,0.25)"}}>
        <div style={{display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:16}}>
          <div>
            <div style={{fontSize:17,fontWeight:800}}>Add Competitor Show{addForm.mode==="bulk"?"s":""}</div>
            <div style={{fontSize:11,color:"#818084",marginTop:2}}>Track competitor shows and enrich their exhibitor lists into leads.</div>
          </div>
          <span onClick={onClose} style={{cursor:"pointer",fontSize:18,color:"#818084",lineHeight:1}}>✕</span>
        </div>

        {ENABLE_BULK_UPLOAD && (
          <div style={{display:"inline-flex",background:"#F4F4F5",borderRadius:9,padding:3,marginBottom:18}}>
            {[["single","Single show"],["bulk","Bulk upload"]].map(([m,lb2])=>(
              <button key={m} onClick={()=>setAddForm(p=>({...p,mode:m}))} style={{padding:"7px 18px",borderRadius:7,border:"none",background:addForm.mode===m?"#fff":"transparent",color:addForm.mode===m?"#26262A":"#AFABAB",fontSize:11.5,fontWeight:addForm.mode===m?700:500,cursor:"pointer",fontFamily:F,boxShadow:addForm.mode===m?"0 1px 3px rgba(16,24,40,0.12)":"none"}}>{lb2}</button>
            ))}
          </div>
        )}

        <div style={{display:"flex",flexDirection:"column",gap:16}}>
          <div>
            <label style={lab}>Linked ITE Event *</label>
            <select value={evId} onChange={e=>setAddForm(p=>({...p,eventId:e.target.value}))} style={{...inp,cursor:"pointer"}}>
              {modalEvents.length === 0 && <option value="">Loading events…</option>}
              {modalEvents.map(e=><option key={e.ite_event_id} value={e.ite_event_id}>{e.name} · {e.sector}</option>)}
            </select>
          </div>

          {addForm.mode==="single" ? <>
            <div style={{display:"grid",gridTemplateColumns:"2fr 1fr",gap:12}}>
              <div style={{position:"relative"}}>
                <label style={lab}>Competitor Show Name *</label>
                <input
                  value={addForm.name}
                  onChange={e => { setAddForm(p=>({...p, name:e.target.value})); setNameDropdownOpen(true); }}
                  onFocus={() => setNameDropdownOpen(true)}
                  onBlur={() => setTimeout(() => setNameDropdownOpen(false), 150)}
                  placeholder="e.g. Gulfood, SIAL, Anuga…"
                  autoComplete="off"
                  style={inp}
                />
                {nameDropdownOpen && nameSuggestions.length > 0 && (
                  <div style={{position:"absolute",top:"calc(100% + 2px)",left:0,right:0,zIndex:200,background:"#fff",border:"1px solid #D8DADC",borderRadius:8,boxShadow:"0 4px 16px rgba(0,0,0,0.12)",maxHeight:200,overflow:"auto"}}>
                    {nameSuggestions.map((s, i) => (
                      <div key={s.canonical}
                        onMouseDown={e => { e.preventDefault(); setAddForm(p => ({...p, name: s.canonical})); setNameDropdownOpen(false); }}
                        style={{padding:"9px 12px",cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:i<nameSuggestions.length-1?"1px solid #F4F4F5":"none"}}
                        onMouseEnter={e=>e.currentTarget.style.background="#FAFAFA"}
                        onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                        <div>
                          <div style={{fontSize:12,fontWeight:600,color:"#26262A"}}>{s.canonical}</div>
                          <div style={{fontSize:9,color:"#818084"}}>existing show · tracked: {s.years.sort().join(", ")}</div>
                        </div>
                        <span style={{fontSize:9,fontWeight:700,color:"#16a34a",background:"#f0fdf4",padding:"2px 7px",borderRadius:4,flexShrink:0}}>+ new year</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div>
                <label style={lab}>Year *</label>
                <select value={addForm.year} onChange={e=>setAddForm(p=>({...p,year:e.target.value}))} style={{...inp,cursor:"pointer"}}>
                  {[THIS_YEAR+1, THIS_YEAR, THIS_YEAR-1, THIS_YEAR-2, THIS_YEAR-3].map(y=>(
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
              </div>
            </div>
            {yearAlreadyTracked && (
              <div style={{background:"#fdecec",border:"1px solid #fbd5d5",borderRadius:6,padding:"7px 10px",fontSize:11.5,color:"#A00F1A",marginTop:-4,display:"flex",alignItems:"flex-start",gap:6}}>
                <span style={{flexShrink:0}}>✕</span>
                <span><strong>{addForm.name} {addForm.year}</strong> is already tracked. Pick a different year, or rename the show if this is a separate edition.</span>
              </div>
            )}
            <div>
              <label style={lab}>Location <span style={{fontWeight:400,color:"#818084"}}>· city / country</span></label>
              <input value={addForm.location} onChange={e=>setAddForm(p=>({...p,location:e.target.value}))} placeholder="e.g. Dubai, UAE" style={inp}/>
            </div>
            <div>
              <label style={lab}>Event Dates</label>
              <DateRangePicker
                start={addForm.startDate}
                end={addForm.endDate}
                hasError={dateErr}
                onChange={(s, e) => setAddForm(p => ({
                  ...p, startDate: s, endDate: e,
                  year: s ? String(new Date(s + "T12:00:00").getFullYear()) : p.year,
                }))}
              />
            </div>
            {dateErr && <div style={{color:"#C1121F",fontSize:11.5,marginTop:-8}}>End date cannot be before start date.</div>}
            <div>
              <label style={lab}>Exhibitor List URL</label>
              <input value={addForm.url} onChange={e=>setAddForm(p=>({...p,url:e.target.value}))} placeholder="https://www.gulfood.com/exhibitors/2026" style={inp}/>
            </div>
          </> : <>
            <div>
              <label style={lab}>Competitor Shows File *</label>
              <div style={{padding:"14px",background:"#FAFAFA",borderRadius:8,border:"1.5px dashed #D8DADC",display:"flex",alignItems:"center",gap:10}}>
                <input ref={bulkFileRef} type="file" accept=".csv" style={{display:"none"}} onChange={e=>{const f=e.target.files[0];if(f)setAddForm(p=>({...p,bulkFileObj:f,bulkFile:f.name}));}}/>
                <button onClick={()=>bulkFileRef.current.click()} style={{padding:"7px 14px",borderRadius:7,border:"1px solid #D8DADC",background:"#fff",fontSize:11,fontWeight:600,cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",gap:4}}><Folder size={14}/> Choose File</button>
                <span style={{fontSize:10,color:addForm.bulkFileObj?"#16a34a":"#818084",fontWeight:addForm.bulkFileObj?700:400}}>{addForm.bulkFile||"one row per show · .csv"}</span>
              </div>
              <div style={{fontSize:9.5,color:"#818084",marginTop:6}}>
                <span onClick={downloadBulkTemplate} style={{color:"#E8431A",cursor:"pointer",fontWeight:600}}>↓ Download template</span>
              </div>
            </div>
          </>}

          <div>
            <label style={lab}>When to run enrichment *</label>
            <div style={{display:"flex",gap:6}}>
              <button onClick={()=>{setAddForm(p=>({...p,runWhen:"now",scheduleDate:""}));setSchedCalOpen(false);}}
                style={{flex:1,padding:"8px",borderRadius:8,border:`1.5px solid ${addForm.runWhen==="now"?"#E8431A":"#D8DADC"}`,background:addForm.runWhen==="now"?"#FCEBE5":"#fff",color:addForm.runWhen==="now"?"#E8431A":"#818084",fontWeight:600,fontSize:11,cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                <span style={{position:"relative",display:"inline-flex",alignItems:"center",gap:4}} onMouseEnter={()=>setRunTip("now")} onMouseLeave={()=>setRunTip(null)}>
                  <Zap size={12}/> Add to queue
                  {runTip==="now"&&<span style={{position:"absolute",bottom:"calc(100% + 8px)",left:"50%",transform:"translateX(-50%)",background:"#26262A",color:"#F4F4F5",fontSize:10.5,fontWeight:400,lineHeight:1.5,padding:"6px 11px",borderRadius:7,whiteSpace:"nowrap",pointerEvents:"none",zIndex:1002,boxShadow:"0 4px 14px rgba(0,0,0,0.28)",fontFamily:F}}>Runs in the next enrichment cycle</span>}
                </span>
              </button>
              {ENABLE_SCHEDULE_LATER && (
                <button ref={schedBtnRef} onClick={()=>{
                    setAddForm(p=>({...p,runWhen:"schedule"}));
                    const r = schedBtnRef.current?.getBoundingClientRect();
                    if (r) setSchedCalPos({top:r.bottom+6,left:r.left});
                    setSchedCalOpen(v=>!v);
                  }}
                  style={{flex:1,padding:"8px",borderRadius:8,border:`1.5px solid ${addForm.runWhen==="schedule"?"#E8431A":"#D8DADC"}`,background:addForm.runWhen==="schedule"?"#FCEBE5":"#fff",color:addForm.runWhen==="schedule"?"#E8431A":"#818084",fontWeight:600,fontSize:11,cursor:"pointer",fontFamily:F,display:"flex",alignItems:"center",justifyContent:"center",gap:4}}>
                  <span style={{position:"relative",display:"inline-flex",alignItems:"center",gap:4}} onMouseEnter={()=>setRunTip("schedule")} onMouseLeave={()=>setRunTip(null)}>
                    <Calendar size={12}/>
                    Schedule later
                    {addForm.scheduleDate && <span style={{fontWeight:400,opacity:0.85}}>· {new Date(addForm.scheduleDate+"T12:00:00").toLocaleDateString("en-GB",{day:"2-digit",month:"short"})}</span>}
                    {runTip==="schedule"&&<span style={{position:"absolute",bottom:"calc(100% + 8px)",left:"50%",transform:"translateX(-50%)",background:"#26262A",color:"#F4F4F5",fontSize:10.5,fontWeight:400,lineHeight:1.5,padding:"6px 11px",borderRadius:7,whiteSpace:"nowrap",pointerEvents:"none",zIndex:1002,boxShadow:"0 4px 14px rgba(0,0,0,0.28)",fontFamily:F}}>Set a date — best run close to the show start</span>}
                  </span>
                </button>
              )}
            </div>
            {ENABLE_SCHEDULE_LATER && (
              <SingleDatePicker open={schedCalOpen} onClose={()=>setSchedCalOpen(false)} pos={schedCalPos}
                value={addForm.scheduleDate} onChange={v=>setAddForm(p=>({...p,scheduleDate:v}))}/>
            )}
          </div>

          <div>
            <label style={lab}>Reviewers</label>
            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
              <div>
                <div style={{fontSize:9.5,fontWeight:700,color:"#5F1156",textTransform:"uppercase",letterSpacing:0.3,marginBottom:4,display:"flex",alignItems:"center",gap:4}}><Megaphone size={12}/> Marketing</div>
                <select value={addForm.mktReviewer} onChange={e=>setAddForm(p=>({...p,mktReviewer:e.target.value}))} style={{...inp,cursor:"pointer",fontSize:11.5}}><option value="">Unassigned</option>{mktUsers.map(u=><option key={u.user_id} value={u.user_id}>{u.name}</option>)}</select>
              </div>
              <div>
                <div style={{fontSize:9.5,fontWeight:700,color:"#E8431A",textTransform:"uppercase",letterSpacing:0.3,marginBottom:4,display:"flex",alignItems:"center",gap:4}}><ClipboardList size={12}/> Sales</div>
                <select value={addForm.salesReviewer} onChange={e=>setAddForm(p=>({...p,salesReviewer:e.target.value}))} style={{...inp,cursor:"pointer",fontSize:11.5}}><option value="">Unassigned</option>{salesUsers.map(u=><option key={u.user_id} value={u.user_id}>{u.name}</option>)}</select>
              </div>
            </div>
          </div>
        </div>

        <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginTop:22,paddingTop:16,borderTop:"1px solid #eef0f3"}}>
          <div style={{fontSize:9,color:"#818084"}}>* Required</div>
          <div style={{display:"flex",gap:8}}>
            <Btn label="Cancel" onClick={onClose}/>
            <button disabled={!canAdd||submitting} onClick={submitAdd} style={{padding:"9px 20px",borderRadius:8,border:"none",background:(canAdd&&!submitting)?"#16a34a":"#D8DADC",color:"#fff",fontSize:12,fontWeight:700,cursor:(canAdd&&!submitting)?"pointer":"default",fontFamily:F,minWidth:100}}>
              {submitting ? "Saving…" : addForm.mode==="bulk" ? "Import shows" : "Add show"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
